import React from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { Theme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../../store/SharedProvider";
import {
  selectSelectedZoneInstance,
  selectZonePlannerMouseState,
} from "../../../../store/room/zone-planner/zone-planner-selectors";
import ZoneControllerMouseState from "../../../../enums/robot/zone-planner/zone-controller-mouse-state";
import { isCleaningZone, isTeachAndRepeatZone } from "../../../../interfaces/robot/rooms/cleaning-zone";
import SelectedZoneForm from "./SelectedZoneForm";
import { isRuleZone } from "../../../../interfaces/robot/rooms/rule-zone";

const SelectedZoneControls: React.FC = () => {
  const dispatch = useSharedDispatch();

  const { setZonePlannerMouseState, setSelectedZone } = useSharedActions();

  const selectedZoneInstance = useSharedSelector((state) => selectSelectedZoneInstance(state));
  const zonePlannerMouseState = useSharedSelector((state) => selectZonePlannerMouseState(state));

  const { t } = useTranslation();

  const matchesMdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));

  if (selectedZoneInstance == null || zonePlannerMouseState !== ZoneControllerMouseState.ShowDetails) {
    return null;
  }

  const isCleaningZoneInstance = isCleaningZone(selectedZoneInstance);
  const isTeachAndRepeatZoneInstance = isTeachAndRepeatZone(selectedZoneInstance);
  const isRuleZoneInstance = isRuleZone(selectedZoneInstance);

  let zoneTypeLabel = t("red-zone");
  if (isCleaningZoneInstance) {
    zoneTypeLabel = t("cleaning-zone");
  }
  if (isTeachAndRepeatZoneInstance) {
    zoneTypeLabel = t("teachandrepeat-zone");
  }
  if (isRuleZoneInstance) {
    zoneTypeLabel = t("rule-zone");
  }

  const handleContentDialogClose = () => {
    dispatch(setSelectedZone(null));
    dispatch(setZonePlannerMouseState(ZoneControllerMouseState.Default));
  };

  const content = (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="body1">{`${zoneTypeLabel}: ${selectedZoneInstance.name}`}</Typography>
      </Grid>
      <Grid item xs={12}>
        <SelectedZoneForm zone={selectedZoneInstance} key={selectedZoneInstance.id} />
      </Grid>
    </Grid>
  );

  return (
    <React.Fragment>
      {matchesMdUp ? (
        <Card
          sx={(theme) => ({
            position: "absolute",
            right: 0,
            zIndex: 2,
            marginTop: theme.spacing(1),
            marginRight: theme.spacing(10),
            width: theme.spacing(48),
            overflow: "auto",
            maxHeight: `calc(100% - ${theme.spacing(2)})`,
          })}
        >
          <CardContent>{content}</CardContent>
        </Card>
      ) : (
        <Dialog open={true} onClose={handleContentDialogClose} fullWidth maxWidth="xs">
          <DialogContent>{content}</DialogContent>
        </Dialog>
      )}
    </React.Fragment>
  );
};

export default SelectedZoneControls;
